/**
 * Role-Based Signup — DeCarbonIQ
 *
 * 2-step flow:
 *   Step 1: Account details (name, email, password)
 *   Step 2: Role-specific organization fields
 *
 * After signup → role-specific first action + micro-onboarding welcome
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Eye, EyeOff, ArrowRight, ArrowLeft, CheckCircle2,
  Building2, BarChart3, GitBranch, Landmark
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const ROLES = {
  sme: {
    label:      "SME",
    icon:       Building2,
    color:      "#059669",
    title:      "Set up your business",
    dest:       "/sme/journey",
    message:    "Let's calculate your emissions in 2 minutes.",
    subMessage: "Step 1 is already complete — your profile is confirmed.",
  },
  corporate: {
    label:      "Corporate",
    icon:       BarChart3,
    color:      "#0891B2",
    title:      "Set up your organisation",
    dest:       "/corporate",
    message:    "Start by setting up your organisation structure.",
    subMessage: "Add your first site to begin tracking group-wide emissions.",
  },
  project: {
    label:      "Project Developer",
    icon:       GitBranch,
    color:      "#7C3AED",
    title:      "Define your project",
    dest:       "/assessments",
    message:    "Define your baseline to estimate project impact.",
    subMessage: "Create your first assessment to start structuring your climate asset.",
  },
  fi: {
    label:      "Financial Institution",
    icon:       Landmark,
    color:      "#D97706",
    title:      "Set up your institution",
    dest:       "/fi/requests",
    message:    "Review incoming climate finance deals.",
    subMessage: "Your pipeline will show finance applications from verified SMEs.",
  },
};

const SECTORS = [
  "Energy / Gas", "Manufacturing", "Construction", "Agriculture",
  "Transport / Logistics", "Waste / Circularity", "Services", "Other"
];
const COUNTRIES = [
  "Nigeria", "Ghana", "South Africa", "Kenya", "Ethiopia",
  "Tanzania", "Uganda", "Côte d'Ivoire", "Senegal", "Other"
];
const FI_TYPES   = ["Commercial Bank", "Development Finance Institution (DFI)", "Climate Fund", "Private Equity / Investor", "Other"];
const FI_REGIONS = ["West Africa", "East Africa", "Southern Africa", "North Africa", "Pan-Africa", "Europe", "Other"];

// Role-specific org form fields
function OrgFields({ role, form, setForm }) {
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const input = (label, key, placeholder, opts = {}) => (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <input type={opts.type || "text"} value={form[key] || ""}
        onChange={e => set(key, e.target.value)}
        placeholder={placeholder} required={opts.required !== false}
        className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        data-testid={`signup-${key}`} />
    </div>
  );
  const select = (label, key, options, opts = {}) => (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <select value={form[key] || ""} onChange={e => set(key, e.target.value)}
        required={opts.required !== false}
        className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
        data-testid={`signup-${key}`}>
        <option value="">{opts.placeholder || "Select..."}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  if (role === "sme") return (
    <div className="space-y-3">
      {input("Company Name", "org_name", "e.g. Gasavant Africa Ltd", { required: true })}
      {select("Industry / Sector", "sector", SECTORS)}
      {select("Country", "country", COUNTRIES)}
      {input("Number of Employees (optional)", "employee_count", "e.g. 45", { type: "number", required: false })}
    </div>
  );

  if (role === "corporate") return (
    <div className="space-y-3">
      {input("Organisation Name", "org_name", "e.g. AfroCement Group", { required: true })}
      {select("Industry", "sector", SECTORS)}
      {select("Primary Country", "country", COUNTRIES)}
    </div>
  );

  if (role === "project") return (
    <div className="space-y-3">
      {input("Project / Organisation Name", "org_name", "e.g. Oko-Oba Biogas Initiative", { required: true })}
      {select("Project Sector", "sector", SECTORS)}
      {select("Host Country", "country", COUNTRIES)}
    </div>
  );

  if (role === "fi") return (
    <div className="space-y-3">
      {input("Institution Name", "org_name", "e.g. Keystone Advisory", { required: true })}
      {select("Institution Type", "org_type", FI_TYPES)}
      {select("Primary Region", "region", FI_REGIONS)}
    </div>
  );

  return null;
}

export default function Signup() {
  const { register }    = useAuth();
  const navigate        = useNavigate();
  const [params]        = useSearchParams();
  const roleParam       = params.get("role");
  const validRole       = ROLES[roleParam] ? roleParam : "sme";

  const [step,     setStep]     = useState(1);  // 1 = account, 2 = org
  const [role,     setRole]     = useState(validRole);
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    org_name: "", sector: "", country: "", employee_count: "",
    org_type: "", region: "",
  });

  const roleConf  = ROLES[role];
  const RoleIcon  = roleConf.icon;

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      setError("Please fill in all fields."); return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters."); return;
    }
    setError("");
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.org_name?.trim()) { setError("Please enter your organisation name."); return; }
    setLoading(true);
    setError("");
    try {
      // Store role-specific destination BEFORE register() sets user state
      sessionStorage.setItem("decarboniq_signup_dest", roleConf.dest);
      await register(form.email, form.password, form.first_name, form.last_name, {
        role,
        org_name:       form.org_name,
        sector:         form.sector || null,
        country:        form.country || null,
        region:         form.region || null,
        org_type:       form.org_type || null,
        employee_count: form.employee_count ? parseInt(form.employee_count) : null,
      });
      toast.success("Account created successfully!");
      // Store micro-onboarding flags
      sessionStorage.setItem("decarboniq_welcome_role", role);
      if (role === "sme") {
        sessionStorage.setItem("decarboniq_new_user", "sme");
      }
      // The account is pending_approval — show pending state, don't redirect to workspace
      // PublicOnlyRoute handles destination
      sessionStorage.setItem("decarboniq_signup_dest", "/pending-approval");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Form panel */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <Link to="/" className="inline-block mb-10">
            <div className="bg-[#0F172A] rounded-xl px-4 py-2 inline-block">
              <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
          </Link>

          {/* Role badge */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${roleConf.color}20` }}>
              <RoleIcon size={14} style={{ color: roleConf.color }} />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: roleConf.color }}>
              {roleConf.label} Workspace
            </span>
            <button onClick={() => navigate("/get-started")} className="ml-auto text-xs text-slate-400 hover:text-slate-600">
              Change role →
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mb-8">
            {[1, 2].map(n => (
              <React.Fragment key={n}>
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
                  step === n ? "bg-[#0F172A] text-white" :
                  step > n   ? "bg-[#059669] text-white" :
                               "bg-slate-100 text-slate-400"
                }`}>
                  {step > n ? <CheckCircle2 size={12} /> : n}
                </div>
                <span className={`text-xs font-medium ${step === n ? "text-[#0F172A]" : "text-slate-400"}`}>
                  {n === 1 ? "Your Account" : roleConf.title}
                </span>
                {n < 2 && <div className={`flex-1 h-px ${step > 1 ? "bg-[#059669]" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700"
              data-testid="signup-error">{error}</div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <form onSubmit={handleStep1} className="space-y-4" data-testid="signup-step1">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight tracking-tight mb-1">Create your account</h1>
              <p className="text-slate-400 text-sm mb-6">You'll be in your workspace in under 2 minutes.</p>

              <div className="grid grid-cols-2 gap-4">
                {[["first_name","First Name","Sarah"], ["last_name","Last Name","Okonkwo"]].map(([k, l, p]) => (
                  <div key={k}>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{l}</label>
                    <input type="text" value={form[k]}
                      onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                      placeholder={p} required
                      className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid={`signup-${k}`} />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Work Email</label>
                <input type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="you@organization.com" required
                  className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="signup-email" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Password</label>
                <div className="relative">
                  <input type={showPass ? "text" : "password"} value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min. 8 characters" required
                    className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="signup-password" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    onClick={() => setShowPass(!showPass)}>
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <button type="submit"
                className="w-full h-12 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 mt-2"
                data-testid="signup-next-btn">
                Continue <ArrowRight size={15} />
              </button>
            </form>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-step2">
              <div className="flex items-center gap-2 mb-1">
                <button type="button" onClick={() => setStep(1)} className="text-slate-400 hover:text-slate-600 p-1">
                  <ArrowLeft size={16} />
                </button>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight tracking-tight">{roleConf.title}</h1>
              </div>
              <p className="text-slate-400 text-sm mb-6">This sets up your workspace — you can edit this later.</p>

              <OrgFields role={role} form={form} setForm={setForm} />

              <button type="submit" disabled={loading}
                className="w-full h-12 bg-[#059669] text-white text-sm font-bold rounded-xl hover:bg-[#047857] transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-50"
                data-testid="signup-submit-btn">
                {loading
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating workspace...</>
                  : <>{roleConf.label} Workspace <ArrowRight size={15} /></>
                }
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" className="text-[#059669] font-semibold hover:underline" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>

      {/* Right panel — dark, role-specific */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] flex-col justify-between p-12">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${roleConf.color}20` }}>
            <RoleIcon size={12} style={{ color: roleConf.color }} />
          </div>
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: roleConf.color }}>
            {roleConf.label} Workspace
          </span>
        </div>

        <div>
          <p className="text-slate-400 text-xs uppercase tracking-widest font-bold mb-3">After signup</p>
          <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">{roleConf.message}</h2>
          <p className="text-slate-400 text-sm mb-8">{roleConf.subMessage}</p>

          <div className="space-y-3">
            {step === 1 ? (
              <>
                {[
                  "Takes under 2 minutes",
                  "No technical expertise required",
                  "Start with real data on day one",
                ].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#059669] flex-shrink-0" />
                    <span className="text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </>
            ) : (
              <>
                {[
                  "Organization profile saved",
                  "Workspace ready immediately",
                  role === "sme"       ? "Jump straight to data input" :
                  role === "corporate" ? "Add your first site right away" :
                  role === "fi"        ? "Finance queue ready to review" :
                                         "Create your first assessment",
                ].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#059669] flex-shrink-0" />
                    <span className="text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="text-xs text-slate-700">DeCarbonIQ — Climate Finance Infrastructure</div>
      </div>
    </div>
  );
}
